import * as THREE from 'three/webgpu';
import {
  float,
  length,
  mix,
  oneMinus,
  positionWorld,
  saturate,
  sin,
  smoothstep,
  uv,
  vec2,
  vec3,
} from 'three/tsl';

import { uNight, uPlayerPos, uTime } from '../core/uniforms.js';

/**
 * @file The MAP BOUNDARY material — a glowing, see-through energy wall that
 * brightens as the player walks toward it. Factory only; the ring geometry and
 * the collider ring live in `world/Boundary.js`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A TRANSLUCENT BARRIER AND NOT A WALL
 * ─────────────────────────────────────────────────────────────────────────────
 * The map edge used to be four invisible AABBs: the player sprinted away from a
 * swarm, hit nothing, and stopped dead. The obvious fix — build something solid
 * there — walks straight into the project's oldest measurement: at the 52°
 * gameplay pitch a wall of height *h* hides **h × 0.78 m** of ground behind it.
 * Anything tall enough to read from across the map swallows the player whenever
 * they stand next to it, which is a worse bug than the one it fixes.
 *
 * Transparency sidesteps the trap completely. The barrier is 3 m tall and you
 * can see straight through it, so it occludes nothing; what makes it READ is
 * that it lights up when you approach and is nearly invisible when you don't.
 * That also solves the vista problem: four glowing walls permanently at full
 * brightness would haze the horizon from anywhere on the map.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `uPlayerPos` — the reserved consumer, finally used
 * ─────────────────────────────────────────────────────────────────────────────
 * `core/uniforms.js` has carried `uPlayerPos` since T5, written every frame by
 * `Player.update`, with no reader. THIS is what it was reserved for. Everything
 * interesting here happens in `opacityNode`, per fragment, against that one
 * uniform — there is no CPU-side work per frame and no per-wall state, so a
 * level can have as many barriers as it likes for the cost of the draws.
 *
 * The opacity is four terms multiplied together:
 *
 *   1. PROXIMITY  `1` inside `fadeNear`, `0` past `fadeFar` (XZ distance from
 *      the fragment to the player). This is the "lights up as you approach".
 *   2. AMBIENT    a small constant (`ambient`, ~0.05) so the wall is faintly
 *      discoverable before you are on top of it — itself gated by a WIDER ramp
 *      out to `ambientFar`, otherwise the far side of the map hazes the frame.
 *   3. VERTICAL   brightest at the ground line, `0` at the top edge, so the
 *      barrier has no hard top line to read as a wall.
 *   4. PATTERN    two scrolling sine bands in world Y plus a horizontal pulse
 *      along the wall. Scanline sci-fi, not disco — it stays within ±20%.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BLOOM
 * ─────────────────────────────────────────────────────────────────────────────
 * `NoToneMapping` means the tint may exceed 1 and that is what feeds bloom —
 * but the night bloom threshold is LOW (city 0.45, wasteland 0.4), so a barrier
 * tuned to look right at noon blows out into a white slab after dusk. Hence
 * `nightScale`: the whole thing dims by ~30% as `uNight` goes to 1, which lands
 * it just over the night threshold instead of far past it.
 *
 * Additive blending, `depthWrite: false`, `fog: false`. Fog is off on purpose —
 * an additive surface multiplied toward the fog colour and then added is just a
 * brighter surface, which is the opposite of what fog is for, and the forest's
 * 2× density would otherwise erase the barrier at exactly the range where it is
 * supposed to be warning you.
 *
 * @module engine/render/barrier
 */

/** @typedef {import('three/tsl').ShaderNodeObject<any>} TSLNode */
/** @typedef {THREE.Color|number|string|[number,number,number]} ColorLike */

/**
 * Electric cyan-teal. Deliberately over 1 in blue/green: this is a bloom source,
 * not a surface albedo.
 * @type {[number, number, number]}
 */
export const BARRIER_TINT = [0.35, 1.0, 1.1];

/**
 * @param {ColorLike} c
 * @returns {[number, number, number]} Linear RGB triple (no colour-space
 *   conversion — same convention as `render/materials.js#rgb`).
 */
function rgb(c) {
  if (Array.isArray(c)) return [c[0], c[1], c[2]];
  const col = c instanceof THREE.Color ? c : new THREE.Color(c);
  return [col.r, col.g, col.b];
}

/**
 * The energy-barrier material.
 *
 * Expects geometry with a `uv` attribute laid out by `world/Boundary.js`:
 * `uv.x` = metres ALONG the wall (not normalised — the pattern must have a
 * constant physical scale whether the wall is 60 m or 240 m long), `uv.y` = 0 at
 * the ground line and 1 at the top edge. The buried skirt a boundary sinks below
 * the terrain carries `uv.y < 0`; the fade saturates, so it simply reads as
 * "full brightness, underground, invisible".
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.emissive`.
 *
 * @param {Object} [o]
 * @param {ColorLike} [o.color=BARRIER_TINT] Tint; components may exceed 1.
 * @param {number} [o.intensity=1] Day multiplier on the tint.
 * @param {number} [o.nightScale=0.72] Multiplier applied to `intensity` at
 *   `uNight = 1`. Below 1 on purpose — see the BLOOM note above.
 * @param {number} [o.fadeNear=4] Metres: inside this the barrier is at full
 *   `peak` opacity.
 * @param {number} [o.fadeFar=14] Metres: past this only `ambient` is left.
 * @param {number} [o.peak=0.85] Opacity at `fadeNear`. 1.0 reads as a solid
 *   sheet at the corners, where two walls overlap additively.
 * @param {number} [o.ambient=0.05] Baseline opacity so a wall you have not
 *   approached is still discoverable.
 * @param {number} [o.ambientFar=30] Metres: past this even the baseline is
 *   gone, so distant walls never haze the vista.
 * @param {number} [o.patternAmount=1] Scales the animated pattern (0 = a clean
 *   flat sheet, 1 = the tuned default, >1 for a debug view of the bands).
 * @param {number} [o.verticalPower=1.35] Exponent on the ground→top fade. >1
 *   keeps the glow hugging the floor; 1 is a straight linear ramp.
 * @param {string} [o.name='barrier']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function barrierMaterial(o = {}) {
  const {
    color = BARRIER_TINT,
    intensity = 1,
    nightScale = 0.72,
    fadeNear = 4,
    fadeFar = 14,
    peak = 0.85,
    ambient = 0.05,
    ambientFar = 30,
    patternAmount = 1,
    verticalPower = 1.35,
    name = 'barrier',
  } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.blending = THREE.AdditiveBlending;
  m.fog = false;
  // See the note at the top of `render/nodes.js`: NEVER true. There is no bake
  // on this geometry at all, but leaving the flag on would multiply `colorNode`
  // by an absent attribute and r185 warns.
  m.vertexColors = false;
  m.toneMapped = false;

  // ── colour ─────────────────────────────────────────────────────────────────
  const c = rgb(color);
  const k = mix(float(intensity), float(intensity * nightScale), uNight);
  m.colorNode = vec3(c[0], c[1], c[2]).mul(k);

  // ── opacity ────────────────────────────────────────────────────────────────
  // XZ only: a barrier should not dim because the player is standing on an
  // embankment, and the walls are 3 m tall against a 14 m ramp anyway.
  const d = length(vec2(positionWorld.x.sub(uPlayerPos.x), positionWorld.z.sub(uPlayerPos.z)));

  // `smoothstep(lo, hi, x)` is only defined for lo < hi, so both ramps are
  // written the honest way round and inverted.
  const prox = oneMinus(smoothstep(float(fadeNear), float(fadeFar), d));
  const halo = oneMinus(smoothstep(float(fadeFar), float(ambientFar), d));
  const glow = prox.mul(float(peak)).add(halo.mul(float(ambient)));

  const vFade = saturate(oneMinus(uv().y)).pow(float(verticalPower));

  // Scanlines ride WORLD Y so the band spacing is physical and every wall of
  // every level agrees; the horizontal pulse rides `uv.x`, which is already in
  // metres along the wall. The two vertical rates are incommensurable so the
  // pattern never visibly loops.
  const band = sin(positionWorld.y.mul(2.1).sub(uTime.mul(0.9))).mul(0.1 * patternAmount);
  const swell = sin(positionWorld.y.mul(0.7).add(uTime.mul(0.35))).mul(0.07 * patternAmount);
  const pulse = sin(uv().x.mul(0.45).add(uTime.mul(0.15))).mul(0.06 * patternAmount);
  const pattern = float(1).add(band).add(swell).add(pulse);

  m.opacityNode = saturate(glow.mul(vFade).mul(pattern));

  return m;
}
